import { forwardRef } from "react";
import { vocabularize } from "src/Common/vocab";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";

export const DATE_RANGE_OPTIONS = [
  { key: "past7", label: "Past 7 Days", days: 7 },
  { key: "past30", label: "Past 30 Days", days: 30 },
  { key: "pastQuarter", label: "Past Quarter", days: 90 },
  { key: "custom", label: "Custom Range", days: null },
];

const formatDisplay = (date) => (date ? moment(date).format("MMM D, YYYY") : "");

// Calendar icon from the design.
const CalendarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="17" height="18" viewBox="0 0 17 18" fill="none">
    <path d="M4.16667 3.95833V0.625C4.16667 0.279822 4.44649 0 4.79167 0C5.13684 0 5.41667 0.279822 5.41667 0.625V3.95833C5.41667 4.30351 5.13684 4.58333 4.79167 4.58333C4.44649 4.58333 4.16667 4.30351 4.16667 3.95833Z" fill="#B4BAD5" />
    <path d="M10.8333 3.95833V0.625C10.8333 0.279822 11.1132 0 11.4583 0C11.8035 0 12.0833 0.279822 12.0833 0.625V3.95833C12.0833 4.30351 11.8035 4.58333 11.4583 4.58333C11.1132 4.58333 10.8333 4.30351 10.8333 3.95833Z" fill="#B4BAD5" />
    <path d="M15 3.95833C15 3.38304 14.5336 2.91667 13.9583 2.91667H2.29167C1.71637 2.91667 1.25 3.38304 1.25 3.95833V15.625C1.25 16.2003 1.71637 16.6667 2.29167 16.6667H13.9583C14.5336 16.6667 15 16.2003 15 15.625V3.95833ZM16.25 15.625C16.25 16.8907 15.224 17.9167 13.9583 17.9167H2.29167C1.02601 17.9167 0 16.8907 0 15.625V3.95833C0 2.69268 1.02601 1.66667 2.29167 1.66667H13.9583C15.224 1.66667 16.25 2.69268 16.25 3.95833V15.625Z" fill="#B4BAD5" />
    <path d="M15.625 6.66667C15.9702 6.66667 16.25 6.94649 16.25 7.29167C16.25 7.63684 15.9702 7.91667 15.625 7.91667H0.625C0.279822 7.91667 0 7.63684 0 7.29167C0 6.94649 0.279822 6.66667 0.625 6.66667H15.625Z" fill="#B4BAD5" />
  </svg>
);

/**
 * Date-range selector for the Generate Report modal.
 *
 * selected: one of "past7" | "past30" | "pastQuarter" | "custom"
 * onSelect(key): preset toggle handler
 * customStart / customEnd: Date objects used when selected === "custom"
 * onCustomChange([start, end]): range picker handler
 */
const DateRangeSelector = ({ selected, onSelect, customStart, customEnd, onCustomChange, includeAllOption }) => {
  // "All" (no date filter) is only offered for reports whose service supports it.
  const options = includeAllOption
    ? [{ key: "all", label: "All", days: null }, ...DATE_RANGE_OPTIONS]
    : DATE_RANGE_OPTIONS;

  // For presets, derive the displayed range from "today".
  const presetOption = options.find((option) => option.key === selected);
  const isCustom = selected === "custom";
  const isAll = selected === "all";

  let displayStart = customStart;
  let displayEnd = customEnd;
  if (!isCustom && presetOption?.days) {
    displayEnd = new Date();
    displayStart = moment().subtract(presetOption.days, "days").toDate();
  }

  const displayText = isAll
    ? vocabularize("All work orders (no date filter)")
    : displayStart || displayEnd
    ? `${formatDisplay(displayStart)} - ${formatDisplay(displayEnd)}`
    : "Select a date range";

  // Read-only range display field (40px, #F8F9FE) shown below the preset chips.
  const RangeInput = forwardRef(({ onClick }, ref) => (
    <div
      ref={ref}
      onClick={isCustom ? onClick : undefined}
      className={`w-full h-10 rounded-[6px] outline outline-[1px] outline-offset-[-1px] outline-[#E5E8F3] bg-[#F8F9FE] flex items-center gap-3 px-3 overflow-hidden ${
        isCustom ? "cursor-pointer" : "cursor-default"
      }`}
    >
      <span className="flex-1 font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#5F6477]">
        {displayText}
      </span>
      <CalendarIcon />
    </div>
  ));

  return (
    <div className="w-full flex flex-col gap-3">
      <div className="w-full flex flex-wrap gap-2">
        {options.map((option) => {
          const isSelected = option.key === selected;
          return (
            <button
              key={option.key}
              type="button"
              onClick={() => onSelect(option.key)}
              className={`flex-1 min-w-[110px] p-[10px] rounded-[10px] ring-1 ring-inset flex items-center justify-center text-[14px] leading-[20px] tracking-[0.14px] text-center transition-colors ${
                isSelected
                  ? "bg-[#EAF3FF] ring-[#0065FD] text-[#0065FD] font-OpenSansSemiBold capitalize"
                  : "bg-white ring-[#E5E8F3] text-[#5F6477] font-OpenSansRegular hover:bg-[#F7F9FC]"
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>

      {isCustom ? (
        <DatePicker
          selectsRange
          startDate={customStart}
          endDate={customEnd}
          onChange={onCustomChange}
          customInput={<RangeInput />}
        />
      ) : (
        <RangeInput />
      )}
    </div>
  );
};

export default DateRangeSelector;
